/**
 * Mixin with VideoCall invites functionality
 */
import {
  ANNOUNCEMENT_TYPES,
  INVITE_CREATION_STATUSES,
  INVITE_STATUSES,
  INVITE_TYPES,
  VIDEO_MEMBER_TYPES
} from "@/helpers/constants";

export default {
  computed: {
    /**
     * Sent video call invites
     * @return {Array}
     */
    sentVideoCallInvites() {
      return this.$store.getters["invites/sentVideoCallInvites"];
    },
    /**
     * Banner's headers
     * @return {Object}
     */
    systemHeaders() {
      return this.$store.getters["config/systemHeaders"];
    },

    /**
     * Banner's messages
     * @return {Object}
     */
    systemMessages() {
      return this.$store.getters["config/systemMessages"];
    },
    /**
     * Announcements config
     * @return {Object}
     */
    announcementsConfig() {
      return this.$store.state.event.config.defaultAnnouncementsConfig;
    }
  },
  methods: {
    /**
     * Sent a few VideoCall invites
     * @param userUUIDs {Array}
     */
    createVideoCallInvites(userUUIDs) {
      // TODO: implement locks and refactor videochats
      if (!this.$store.state.activeVideoChat) {
        const firstUserUUID = userUUIDs.pop();
        this.createVideoCallInvite(firstUserUUID, true).then(() => {
          userUUIDs.forEach(async userUUID => await this.createVideoCallInvite(userUUID, true));
        });
      } else {
        userUUIDs.forEach(async userUUID => await this.createVideoCallInvite(userUUID, true));
      }
    },

    createVideoChatMember(userUUID, invite = null) {
      this.$store.commit("videoChat/setVideoChatMember", {
        userUUID: userUUID,
        data: {
          memberType: VIDEO_MEMBER_TYPES.INVITEE,
          invite: invite || { inviteStatus: INVITE_STATUSES.SEND },
          connection: null,
          stream: null
        }
      });
    },

    /**
     * Create a new VideoCall invite
     * @param userUUID {String}
     * @param blockScroll {Boolean}
     * @return {Promise<void>}
     */
    async createVideoCallInvite(userUUID, blockScroll) {
      // Create Draft user
      this.createVideoChatMember(userUUID);

      if (!blockScroll) {
        this.$scrollTo("#digital-event-room-video", 500);
      }

      this.$store.commit("invites/setVideoCallInviteCreationStatus", INVITE_CREATION_STATUSES.IN_PROCESS);

      let videoChat = await this.$store.dispatch("videoChat/getOrCreateVideoChat", userUUID);

      let sentInvite = await this.$store.dispatch("invites/createVideoCallInvite", {
        videoChatUUID: videoChat.groupUUID,
        userUUID: userUUID
      });

      this.createVideoChatMember(userUUID, sentInvite);

      this.$store.commit("invites/setVideoCallInviteCreationStatus", INVITE_CREATION_STATUSES.CREATED);

      if (!this.$store.state.videoChat.activeVideoChat && this.sentVideoCallInvites.length === 1) {
        this.$store.commit("videoChat/setVideoCallInitiator", this.$store.state.users.user.user_uuid);
      }
    },

    /**
     * Force disconnect from opentok session
     * @param connection {Object}
     * @param successHandler {Function}
     */
    forceDisconnect(connection, successHandler) {
      if (this.$store.state.videoChat.activeVideoSession) {
        this.$store.state.videoChat.activeVideoSession.forceDisconnect(connection, async error => {
          if (error) {
            // eslint-disable-next-line no-console
            console.error("Force Disconnect ERROR", connection, error);
          } else if (successHandler) {
            await successHandler();
          }
        });
      }
    },

    /**
     * Re-send VideoCall invite
     * @param connection {Object}
     * @return {Promise<void>}
     */
    reSendVideoCallInvite(connection) {
      this.forceDisconnect(connection, async () => {
        let invite = this.$store.getters["invites/sentVideoCallInvites"].find(
          invite => invite.inviteeUUID === connection.data
        );
        if (invite) {
          this.$store.commit("invites/deleteSentInvite", invite);
        }
        await this.createVideoCallInvite(connection.data, null);
      });
    },

    /**
     * Cancel video call invite
     * @param invite {Object}
     * @return {Promise<void>}
     */
    async cancelVideoCallInvite(invite) {
      await this.$store.dispatch("invites/cancelVideoCallInvite", invite);
    },

    /**
     * Cancel all video call sent invites
     * @return {Promise<void>}
     */
    async cancelVideoCallInvites() {
      await this.$store.dispatch("invites/cancelVideoCallInvites");
    },

    /**
     * Show Video Call invite
     * @param invite {Object}
     * @return {Promise<void>}
     */
    async showVideoCallInvite(invite) {
      await this.$store.dispatch("videoChat/logVideoChatsAction", {
        actionName: "GET_VIDEO_CALL_INVITE"
      });

      let header = this.systemHeaders.VIDEO_CALL_INVITE;
      let body = this.systemMessages.VIDEO_CALL_INVITE.format(invite.messageFrom, invite.messageBody.tableName);
      let message = `${header} ${body}`;
      let announcementConfig = this.announcementsConfig[ANNOUNCEMENT_TYPES.VIDEO_CALL_INVITE_SEND];

      let notification = this.$notify(
        Object.assign(
          {
            type: "warning",
            HTMLMessage: message,
            buttons: [
              {
                name: "Accept",
                type: "success",
                handler: async () => {
                  notification.close();
                  await this.acceptVideoCallInvite(invite);
                },
                icon: "fa fa-phone"
              },
              {
                name: "Decline",
                type: "danger",
                handler: async () => {
                  notification.close();
                  await this.declineVideoCallInvite(invite, INVITE_STATUSES.DECLINE_BY_INVITEE);
                },
                showCountdown: true,
                icon: "fa fa-ban"
              }
            ],
            multiLine: true,
            hideIcon: true,
            top: true,
            right: true,
            showClose: false,
            closeDelay: null,
            showCountdown: announcementConfig.closeDelay
          },
          announcementConfig || {},
          { closeDelay: null }
        )
      );

      let timeoutId = setTimeout(async () => {
        notification.close();
        await this.declineVideoCallInvite(invite, INVITE_STATUSES.DECLINE_BY_TIMEOUT);
      }, announcementConfig.closeDelay || 20000);
      this.$store.commit("invites/addDisplayingInvite", {
        invite,
        notification,
        timeoutId
      });
    },

    /**
     * Accept Video Call invite
     * @param invite {Object}
     * @return {Promise<void>}
     */
    async acceptVideoCallInvite(invite) {
      this.$store.commit("videoChat/setVideoCallInitiator", null);

      await this.cancelVideoCallInvites();

      // Add all current users to the call
      (invite.messageBody.members || []).forEach(memberUUID => {
        this.createVideoChatMember(memberUUID, { inviteStatus: INVITE_STATUSES.ACCEPT });
      });

      this.$scrollTo("#digital-event-room-video", 500);

      await this.$store.dispatch("videoChat/logVideoChatsAction", { actionName: "ACCEPT_VIDEO_CALL_INVITE" });

      // Cancel delayed cancel
      clearTimeout(this.displayingReceivedInvites[invite.uuid].timeoutId);

      // Leave previous video call (if exist)
      await this.$store.dispatch("videoChat/leaveVideoCall");

      // Decline all other received invites
      this.receivedInvites.forEach(noRelevantInvite => {
        if (noRelevantInvite.inviteType === INVITE_TYPES.VIDEO_CALL && noRelevantInvite.uuid !== invite.uuid) {
          this.declineVideoCallInvite(noRelevantInvite, INVITE_STATUSES.DECLINE_BY_ACCEPTING_ANOTHER_CALL);
        }
      });

      let { success } = await this.$store.dispatch("videoChat/joinVideoCall", {
        videoChatUUID: invite.groupUUID,
        recipientUUID: invite.inviterUUID
      });

      if (success) {
        await this.$store.dispatch("invites/updateInvite", {
          invite: invite,
          data: {
            inviteStatus: INVITE_STATUSES.ACCEPT
          }
        });

        // Change current user to publisher
        this.$store.commit("videoChat/setVideoChatMember", {
          userUUID: this.$store.state.users.user.user_uuid,
          data: {
            memberType: VIDEO_MEMBER_TYPES.PUBLISHER
          }
        });
      }

      this.hideInvite(invite);
    },

    /**
     * Decline Video Call invite
     * @param invite {Object}
     * @param inviteStatus {String}
     * @return {Promise<void>}
     */
    async declineVideoCallInvite(invite, inviteStatus) {
      await this.$store.dispatch("videoChat/logVideoChatsAction", {
        actionName: "DECLINE_VIDEO_CALL_INVITE"
      });

      // Cancel delayed cancel
      clearTimeout(this.displayingReceivedInvites[invite.uuid].timeoutId);

      await this.$store.dispatch("invites/updateInvite", {
        invite: invite,
        data: {
          inviteStatus: inviteStatus
        }
      });

      this.hideInvite(invite);
    }
  }
};
